<template>
  <div>
    <h5 class="mb-4"><strong>What would you like to say?</strong></h5>

    <b-alert v-if="errorMessage" variant="danger" show class="mb-3">
      {{ errorMessage }}
    </b-alert>

    <div class="mb-4">
      <div class="section-label">
        {{ recipientCount === 1 ? 'Message to recipient' : 'Message to recipients' }}
      </div>
      <b-form-textarea
        :value="message"
        placeholder="Write a message to the recipient..."
        rows="3"
        max-rows="6"
        :state="messageState"
        @input="$emit('update:message', $event)"
        @blur="messageTouched = true"
      />
      <small v-if="messageState === false" class="text-danger">Please write a message.</small>
    </div>

    <div v-if="companyValues.length > 0" class="mb-4">
      <div class="section-label">Select qualities</div>
      <div class="value-chips">
        <span
          v-for="value in companyValues"
          :key="value"
          :class="['value-chip', { 'value-chip--selected': selectedCompanyValues.includes(value) }]"
          role="button"
          @click="toggleCompanyValue(value)"
        >
          {{ value }}
        </span>
      </div>
    </div>

    <div v-if="awardAmounts.length > 0" class="mb-3">
      <b-form-checkbox :checked="includeReward" @change="$emit('toggle-reward', $event)">
        <strong>Add a reward</strong>
        <br />
        <small class="text-muted">Select the value they'll receive</small>
      </b-form-checkbox>

      <div v-if="includeReward" class="amount-pills mt-2 ml-4">
        <span
          v-for="amount in awardAmounts"
          :key="amount"
          :class="['amount-pill', { 'amount-pill--selected': amountMinor === amount }]"
          role="button"
          @click="$emit('update:amountMinor', amount)"
        >
          {{ formatAmount(amount) }}
        </span>
      </div>
    </div>

    <div v-else-if="isAdjustableAmount" class="mb-3">
      <b-form-checkbox :checked="includeReward" @change="$emit('toggle-reward', $event)">
        <strong>Add a reward</strong>
        <br />
        <small class="text-muted">
          Enter the value they'll receive<template v-if="maxAwardValueMinor !== null">
            (up to {{ formatAmount(maxAwardValueMinor) }})</template
          >
        </small>
      </b-form-checkbox>

      <div v-if="includeReward" class="mt-2 ml-4">
        <b-form-input
          :value="typedAmount"
          type="number"
          min="0"
          step="0.01"
          inputmode="decimal"
          placeholder="0.00"
          @input="$emit('update:typedAmount', $event)"
        />
        <small v-if="amountError" class="text-danger">{{ amountError }}</small>
      </div>
    </div>

    <div v-if="showBudget" class="mb-3">
      <budget-gauge
        v-if="hasBudgetControls"
        :validation="validation"
        :pending-amount-minor="totalAmountMinor"
        :class="{ 'budget--checking': isCheckingBudget }"
      />
      <b-alert v-if="budgetError" variant="danger" show class="mb-0 mt-2">{{ budgetError }}</b-alert>
    </div>

    <div v-if="showCategorySelector" class="mb-3">
      <div class="section-label">Spend category<span v-if="categoryRequired"> *</span></div>
      <div class="value-chips">
        <span
          v-for="option in categories"
          :key="option"
          :class="['value-chip', { 'value-chip--selected': category === option }]"
          role="button"
          @click="$emit('update:category', category === option ? null : option)"
        >
          {{ option }}
        </span>
      </div>
      <small v-if="categoryRequired && !category" class="text-danger">A category is required.</small>
    </div>

    <div class="privacy-block">
      <b-form-checkbox :checked="isPrivate" @change="$emit('update:isPrivate', $event)">
        <strong>Make private</strong>
        <br />
        <small class="text-muted">Hide from company feed</small>
      </b-form-checkbox>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, toRef } from 'vue';
import BudgetGauge from '@/pages/recognition/components/BudgetGauge.vue';
import useAwardGivingConfig from '@/pages/recognition/composables/useAwardGivingConfig';
import { validationErrorMessage } from '@/pages/recognition/utils/giveRecognition';
import { toCurrency } from '@/utils';

const props = defineProps({
  program: {
    type: Object,
    default: null,
  },
  recipientCount: {
    type: Number,
    default: 0,
  },
  message: {
    type: String,
    default: '',
  },
  selectedCompanyValues: {
    type: Array,
    default: () => [],
  },
  includeReward: {
    type: Boolean,
    default: false,
  },
  amountMinor: {
    type: Number,
    default: null,
  },
  typedAmount: {
    type: String,
    default: '',
  },
  category: {
    type: String,
    default: null,
  },
  isPrivate: {
    type: Boolean,
    default: false,
  },
  amountError: {
    type: String,
    default: '',
  },
  validation: {
    type: Object,
    default: null,
  },
  isCheckingBudget: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:message',
  'update:selectedCompanyValues',
  'update:amountMinor',
  'update:typedAmount',
  'update:category',
  'update:isPrivate',
  'toggle-reward',
]);

const messageTouched = ref(false);

const { companyValues, awardAmounts, maxAwardValueMinor, isAdjustableAmount, categories, categoryRequired } =
  useAwardGivingConfig(toRef(props, 'program'));

const messageState = computed(() => (messageTouched.value && !props.message.trim() ? false : null));

const totalAmountMinor = computed(() =>
  props.includeReward && props.amountMinor ? props.amountMinor * props.recipientCount : 0,
);

const hasBudgetControls = computed(() => !!props.validation?.program_budget_minor);

const budgetError = computed(() => validationErrorMessage(props.validation));

const showBudget = computed(
  () => props.includeReward && !!props.validation && (hasBudgetControls.value || !!budgetError.value),
);

// The spend category is chosen by the finaliser only when a monetary reward is being
// given and the program has categories configured.
const showCategorySelector = computed(() => props.includeReward && categories.value.length > 0);

function toggleCompanyValue(value) {
  const next = props.selectedCompanyValues.includes(value)
    ? props.selectedCompanyValues.filter(v => v !== value)
    : [...props.selectedCompanyValues, value];
  emit('update:selectedCompanyValues', next);
}

function formatAmount(amount) {
  return toCurrency(amount);
}

defineExpose({ touchMessage: () => (messageTouched.value = true) });
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/give-recognition';

.privacy-block {
  padding-top: 1rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

.budget--checking {
  opacity: 0.5;
  transition: opacity 0.3s ease-out;
}
</style>
